import { useMemo } from "react";
import { useAppointments } from "@/features/appointments/hooks/appointmentQueries";
import type { Appointment } from "@/features/appointments/types/appointmentTypes";
import { businessDayBounds } from "@/features/appointments/utils/bookingTime";
import {
  useAvailabilities,
  useAvailabilityOverridesForRanges,
  useAvailableTimeSlotsForRanges,
} from "@/features/availability/hooks/useAvailabilities";
import { businessHoursForDay, scopedSchedules } from "@/features/availability/utils/businessHours";
import { generateAvailabilityGaps } from "@/features/availability/utils/generateAvailabilityTimeSlots";
import { useCalendarEventsForRanges } from "@/features/calendar-events/hooks/calendarEventQueries";
import { getBusinessDateKey } from "@/shared/utils/formatters";
import type { AgendaDays } from "../utils/agendaDays";

const DAY_MS = 86_400_000;
const hiddenStatuses: Appointment["status"][] = ["cancelled", "declined", "no_show"];

export function useAgendaRange(params: {
  days: AgendaDays;
  firstDay: number;
  lastDay: number;
  userId: string;
  businessId: string;
  locationId: string;
  timezone: string;
  now: Date;
}) {
  const { days, userId, businessId, locationId, timezone, now } = params;
  const scope = { userId, businessId, locationId };
  const firstWeek = Math.max(0, days.weekOfDay(params.firstDay) - 1);
  const lastWeek = Math.min(days.weekCount - 1, days.weekOfDay(params.lastDay) + 1);

  const weeks = useMemo(() => Array.from({ length: lastWeek - firstWeek + 1 }, (_, offset) => {
    const keys = days.weekDayKeys(firstWeek + offset);
    return {
      week: firstWeek + offset,
      startDate: keys[0],
      endDate: keys[6],
      startAt: businessDayBounds(keys[0], timezone).startAt,
      endAt: businessDayBounds(keys[6], timezone).endAt,
    };
  }), [days, firstWeek, lastWeek, timezone]);
  // Include neighbouring days so cross-midnight buffers are checked by the server.
  const slotRanges = useMemo(() => weeks.map((week) => ({
    startAt: new Date(week.startAt.getTime() - DAY_MS),
    endAt: new Date(week.endAt.getTime() + DAY_MS),
  })), [weeks]);

  const appointments = useAppointments();
  const availability = useAvailabilities(scope);
  const events = useCalendarEventsForRanges(weeks);
  const overrides = useAvailabilityOverridesForRanges(scope, weeks);
  const timeSlots = useAvailableTimeSlotsForRanges(scope, slotRanges);

  const appointmentsByDay = useMemo(() => {
    const byDay = new Map<string, Appointment[]>();
    for (const appointment of appointments.data ?? []) {
      if (appointment.businessId !== businessId || appointment.locationId !== locationId || !appointment.startAt
        || hiddenStatuses.includes(appointment.status)) continue;
      const key = getBusinessDateKey(appointment.startAt, timezone);
      byDay.set(key, [...(byDay.get(key) ?? []), appointment]);
    }
    return byDay;
  }, [appointments.data, businessId, locationId, timezone]);

  const schedule = scopedSchedules(availability.data ?? [], scope).active;
  const error = appointments.error ?? availability.error ?? events.error ?? overrides.error ?? timeSlots.error;
  const retry = () => {
    void appointments.refetch();
    void availability.refetch();
    void events.refetch();
    void overrides.refetch();
    void timeSlots.refetch();
  };

  const dayData = (dateKey: string) => {
    const dayIndex = days.dayIndexOf(dateKey);
    const chunk = dayIndex === null ? -1 : weeks.findIndex((week) => week.week === days.weekOfDay(dayIndex));
    const dayEvents = events.data[chunk];
    const dayOverrides = overrides.data[chunk];
    const daySlots = timeSlots.data[chunk];
    const isLoading = !error && (!appointments.data || !availability.data || !dayEvents || !dayOverrides || !daySlots);
    const bounds = businessDayBounds(dateKey, timezone);
    return {
      appointments: appointmentsByDay.get(dateKey) ?? [],
      events: dayEvents ?? [],
      openWindows: businessHoursForDay(schedule, dayOverrides ?? [], dateKey),
      gaps: daySlots ? generateAvailabilityGaps(daySlots, bounds.startAt, bounds.endAt, now) : [],
      isLoading,
      error,
    };
  };

  return { dayData, schedule, scheduleReady: !availability.isLoading, error, retry };
}
